import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Clock, Calendar, CreditCard, ShieldCheck, Lock, Loader2 } from "lucide-react";
import { useTranslation } from "../i18n/I18nContext";
import BundlePackageDetails from "./BundlePackageDetails";

const LEGAL = {
  terms: "https://talkivist.me/terms",
  privacy: "https://talkivist.me/privacy",
  refund: "https://talkivist.me/refund",
  security: "https://talkivist.me/security",
};

const CARD_LOGOS = [
  { src: "/payment-logos/visa.jpg", alt: "Visa", href: "https://www.visa.com" },
  { src: "/payment-logos/mastercard.png", alt: "Mastercard", href: "https://www.mastercard.com" },
  { src: "/payment-logos/maestro.png", alt: "Maestro", href: "https://www.mastercard.com/brandcenter/us/en/home.html" },
];

// Monri/kart şemalarının zorunlu tuttuğu VERBATIM para birimi ibaresi — değiştirme.
const CURRENCY_STATEMENT =
  "All payments will be affected in EUR. The charged amount on your credit card account is converted into your local currency according to the exchange rate of credit card associations.";

const LegalLink = ({ href, children, testid }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="text-red-400 hover:text-red-300 underline underline-offset-2"
    data-testid={testid}
  >
    {children}
  </a>
);

export const PurchaseConfirmModal = ({ open, onOpenChange, item, itemType, onConfirm, loading }) => {
  const { t, language } = useTranslation();
  const [accepted, setAccepted] = useState(false);

  const legalUrl = (key) => `${LEGAL[key]}?lang=${language}`;

  useEffect(() => {
    if (open) setAccepted(false);
  }, [open]);

  if (!item) return null;
  const isBundle = itemType === "bundle";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-neutral-900 border-neutral-800 text-white max-w-md max-h-[90vh] overflow-y-auto"
        data-testid="purchase-confirm-modal"
      >
        <DialogHeader>
          <DialogTitle className="text-white text-xl font-['Nunito'] flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-red-500" />
            {t("po_title")}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          {/* Sipariş özeti */}
          <div className="rounded-xl border border-neutral-800 bg-neutral-800/40 p-4" data-testid="order-summary">
            <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-3">
              {t("po_summary")}
            </p>
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="font-bold text-white truncate" data-testid="order-item-name">{item.name}</p>
                {!isBundle && item.level && (
                  <span className="inline-block mt-1 text-xs px-2 py-0.5 rounded bg-red-600/20 text-red-400 border border-red-600/30">
                    {item.level}
                  </span>
                )}
              </div>
            </div>

            {!isBundle && (
              <ul className="mt-3 space-y-2 text-sm text-neutral-300">
                <li className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-red-500 shrink-0" />
                  <span>{item.total_sessions} {t("sessions_count")} × {item.session_duration_minutes} {t("minutes")}</span>
                </li>
                <li className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-red-500 shrink-0" />
                  <span>{item.duration_days} {t("days_access")}</span>
                </li>
              </ul>
            )}

            {isBundle && Array.isArray(item.packages) && item.packages.length > 0 && (
              <div className="mt-3">
                <p className="text-xs text-neutral-500 mb-2">{t("po_included")}</p>
                <BundlePackageDetails packages={item.packages} />
              </div>
            )}
          </div>

          {/* Tutar */}
          <div className="flex items-end justify-between border-t border-neutral-800 pt-4">
            <div>
              <p className="text-sm text-neutral-400">{t("po_total")}</p>
              <p className="text-[11px] text-neutral-500 mt-0.5" data-testid="vat-note">{t("po_vat_note")}</p>
            </div>
            <div className="text-right">
              {isBundle && item.original_price && (
                <span className="block text-sm text-neutral-500 line-through">€{item.original_price}</span>
              )}
              {isBundle && item.discount_percentage != null && (
                <span className="block text-xs text-green-500 font-semibold" data-testid="modal-discount">%{item.discount_percentage} {t("discount")}</span>
              )}
              <span className="text-3xl font-bold text-white" data-testid="order-total">€{item.price}</span>
              <span className="block text-xs text-neutral-500">EUR</span>
            </div>
          </div>

          {/* Para birimi ibaresi (zorunlu) */}
          <p className="text-[11px] leading-relaxed text-neutral-500 bg-neutral-800/40 rounded-lg p-3" data-testid="currency-statement">
            {CURRENCY_STATEMENT}
          </p>

          {/* Ödeme yöntemi + kart logoları */}
          <div>
            <p className="text-sm text-neutral-400 mb-2">{t("po_pay_method")}</p>
            <div className="flex items-center gap-2">
              {CARD_LOGOS.map((logo) => (
                <a
                  key={logo.alt}
                  href={logo.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white rounded-md p-1.5 flex items-center justify-center h-10 w-14 hover:opacity-90 transition-opacity"
                  data-testid={`card-logo-${logo.alt.toLowerCase()}`}
                  title={logo.alt}
                >
                  <img src={logo.src} alt={logo.alt} className="max-h-7 max-w-full object-contain" />
                </a>
              ))}
            </div>
          </div>

          {/* Zorunlu onay checkbox */}
          <div className="flex items-start gap-3 rounded-lg border border-neutral-800 bg-neutral-800/30 p-3">
            <Checkbox
              id="accept-terms"
              checked={accepted}
              onCheckedChange={(v) => setAccepted(v === true)}
              className="mt-0.5 border border-neutral-300 bg-transparent data-[state=checked]:bg-red-600 data-[state=checked]:border-red-600"
              data-testid="accept-terms-checkbox"
            />
            <label htmlFor="accept-terms" className="text-sm text-neutral-300 leading-relaxed cursor-pointer">
              {t("po_agree_intro")}{" "}
              <LegalLink href={legalUrl("terms")} testid="link-terms">{t("po_terms")}</LegalLink>,{" "}
              <LegalLink href={legalUrl("privacy")} testid="link-privacy">{t("po_privacy")}</LegalLink>{" "}
              {t("po_and")}{" "}
              <LegalLink href={legalUrl("refund")} testid="link-refund">{t("po_refund")}</LegalLink>.
            </label>
          </div>

          {/* Güvenli ödeme rozetleri */}
          <div className="flex items-center justify-between gap-2 border-t border-neutral-800 pt-4">
            <div className="flex items-center gap-2">
              <img src="/payment-logos/monri.png" alt="Monri WebPay" className="h-6 object-contain" data-testid="monri-logo" />
              <span className="bg-white rounded p-1 flex items-center h-7">
                <img src="/payment-logos/visa-secure.jpg" alt="Visa Secure" className="max-h-5 object-contain" />
              </span>
              <span className="bg-white rounded p-1 flex items-center h-7">
                <img src="/payment-logos/mc-idcheck.png" alt="Mastercard ID Check" className="max-h-5 object-contain" />
              </span>
            </div>
            <a
              href={legalUrl("security")}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-[11px] text-neutral-400 hover:text-neutral-200"
              data-testid="link-security"
            >
              <ShieldCheck className="w-3.5 h-3.5 text-green-500" />
              {t("po_secure")}
            </a>
          </div>

          {/* Butonlar */}
          <div className="flex gap-3 pt-1">
            <Button
              variant="outline"
              className="flex-1 border-neutral-700 bg-transparent text-neutral-300 hover:bg-neutral-800 h-12"
              onClick={() => onOpenChange(false)}
              disabled={loading}
              data-testid="confirm-cancel-btn"
            >
              {t("po_cancel")}
            </Button>
            <Button
              className="flex-1 bg-red-600 hover:bg-red-700 text-white h-12 disabled:opacity-50"
              onClick={onConfirm}
              disabled={!accepted || loading}
              data-testid="proceed-payment-btn"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Lock className="w-4 h-4 mr-2" />
                  {t("po_proceed")}
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PurchaseConfirmModal;
